// @ts-nocheck
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { typographyGetNoResultsAltProps } from "@store/Themes/common/props/typography";
import useExpandProps, { ExpandStyles } from "@store/Framework/Common/Hooks/useExpandProps";
import mergeProps from "@store/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@store/Framework/Common/Utilities/unitHelper";
import React, { FC, useMemo, useRef } from "react";
import { css } from "styled-components";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import { useCurrentCartState } from "@store/Framework/Store/Data/Carts/CartsHooks";
import { usePageLinkByPageType } from "@store/Framework/Store/Links/LinksHooks";
import constants from "@store/constants";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import ShoppingCartFill from "@store/Components/Toolkit/Icons/ShoppingCart/ShoppingCartFill";
import { spacingStyles } from "@store/Themes/common/mixins/spacing";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import {
    isCartCheckoutDisabled,
    hasOnlyQuoteRequiredProducts,
    hasQuoteRequiredProducts,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import breakpoint from "@store/Framework/Common/Utilities/breakpoint";
import getWidgetComponent from "@store/Framework/Common/Utilities/getWidgetComponent";
import { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay/LoadingOverlay";
import { CartTotalStyles } from "@store/Overrides/Widgets/Cart/CartTotal";
import InfosBox, { InfosBoxProps } from "@store/Components/Toolkit/InfosBox";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import CartLineCardCondensed, {
    CartLineCardCondensedStyles,
} from "@store/Overrides/Components/CartLineCardCondensed";
import DataViewLoadingOverlay from "@store/Components/Toolkit/DataViewLoadingOverlay";
import { getCurrentPromotionsDataView } from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import ScrollWrapper, { ScrollWrapperPresentationProps } from "@store/Components/Toolkit/ScrollWrapper";
import useIsBreakpoint from "@store/Framework/Common/Hooks/useIsBreakpoint";

const CartTotal = getWidgetComponent("Cart/CartTotal");

const cartLineHeight = 156;
const miniCartSpacing = 12;
const spacingForScrollbar = 10;
const infoBoxSpace = 87;
const mobileBreakpoint = "md";

const headerFooterStyles = (borderPosition: string) => {
    return css`
        display: flex;
        padding: ${pxToRem(miniCartSpacing, 0)};
        margin: ${pxToRem(0, spacingForScrollbar)};
        border-${borderPosition}: 1px solid ${getColor("common.border")};
    `;
};

export interface MiniCartPresentationProps extends ExpandStyles<MiniCartStyles> {
    /** Whether to display checkout buttons in the mini cart footer */
    displayCheckoutButtons?: boolean;
    /** Maximum number of cart line items to display before enabling scroll */
    maxVisibleItems?: number;
}
export interface MiniCartProps extends MiniCartPresentationProps {
    /** Callback function triggered when mini cart is closed */
    onClose: () => void;
}

export interface MiniCartStyles {
    wrapper?: InjectableCss;
    header?: InjectableCss;
    subtotalRow?: InjectableCss;
    itemCount?: TypographyPresentationProps;
    subtotal?: TypographyPresentationProps;
    viewCartButton?: ButtonPresentationProps;
    loadingOverlay?: LoadingOverlayProps;
    centeringWrapper?: InjectableCss;
    noCartLinesIcon?: IconPresentationProps;
    noCartLinesText?: TypographyPresentationProps;
    scrollWrapper?: ScrollWrapperPresentationProps;
    cartLinesContainer?: InjectableCss;
    cartLineCardCondensed?: CartLineCardCondensedStyles;
    footer?: InjectableCss;
    cartTotal?: CartTotalStyles;
    checkoutDisabledMessage?: InfosBoxProps;
}

export const miniCartStyles: MiniCartStyles = {
    wrapper: {
        css: css`
            width: 100%;

            ${breakpoint(mobileBreakpoint)} {
                width: ${pxToRem(700)};
            }
        `,
    },
    header: {
        css: css`
            ${headerFooterStyles("bottom")};
            align-items: center;
            justify-content: space-between;
        `,
    },
    subtotalRow: {
        css: css`
            display: flex;
            flex-direction: column;
            font-size: ${x => x.theme.design.typography.size.smaller};
        `,
    },
    subtotal: {
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    viewCartButton: { sizeVariant: "small" },
    centeringWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            height: ${pxToRem(200)};
            margin-bottom: ${pxToRem(miniCartSpacing)};
        `,
    },
    noCartLinesIcon: {
        src: ShoppingCartFill,
        size: 42,
        color: "primary",
        css: spacingStyles(null, pxToRem(5)),
    },
    noCartLinesText: typographyGetNoResultsAltProps(),
    scrollWrapper: {
        maskSize: 50,
        extendedStyles: {
            wrapper: {
                css: css`
                    overflow-x: hidden;
                    -webkit-overflow-scrolling: touch;
                    scrollbar-width: thin;
                    scrollbar-color: white transparent;

                    &::-webkit-scrollbar-thumb {
                        background: white;
                    }
                `,
            },
        },
    },
    cartLinesContainer: {
        css: css`
            ${spacingStyles(pxToRem(miniCartSpacing), 0)};
            padding: ${pxToRem(0, spacingForScrollbar)};
        `,
    },
    cartLineCardCondensed: {
        container: {
            css: css`
                border-bottom: 0;
                padding-bottom: ${pxToRem(12)};

                &:first-child {
                    padding-top: ${pxToRem(12)};
                }

                &:last-child {
                    padding-bottom: ${pxToRem(12)};
                }
            `,
        },
        infoLeftColumn: { width: [12, 12, 6, 6, 6] },
        productPriceAndQuantityGridItem: { width: [12, 12, 6, 6, 6] },
        productDescription: {
            productDetailLink: {
                css: css`
                    white-space: initial;
                `,
            },
        },
        price: {
            wrapper: {
                css: css`
                    white-space: initial;
                `,
            },
        },
    },
    footer: {
        css: css`
            ${headerFooterStyles("top")};
            justify-content: flex-end;
        `,
    },
    cartTotal: {
        orderActions: {
            removeStickyActions: true,
            extendedStyles: {
                flexWrapper: {
                    content: {
                        css: css`
                            flex-wrap: nowrap;
                        `,
                    },
                    item: {
                        css: css`
                            width: auto;
                        `,
                    },
                },
            },
        },
        orderStickyActions: { removeStickyActions: true },
        cancelPunchOutButton: { sizeVariant: "small" },
        checkoutButton: { sizeVariant: "small" },
        saveOrderButton: { sizeVariant: "small" },
        submitQuoteButton: { sizeVariant: "small" },
        submitRequisitionButton: { sizeVariant: "small" },
    },
    checkoutDisabledMessage: {
        type: "error",
        extendedStyles: {
            wrapper: {
                css: css`
                    white-space: initial;
                    margin-top: 1em;
                    margin-bottom: 0;
                `,
            },
        },
    },
};

const MiniCart: FC<MiniCartProps> = ({
    displayCheckoutButtons,
    extendedStyles,
    maxVisibleItems,
    onClose,
    overridedStyles,
}) => {
    const styles = useExpandProps(miniCartStyles, extendedStyles, overridedStyles);
    const history = useHistory();
    const cartLink = usePageLinkByPageType(constants.pageType.cart);
    const { isUpdatingCart } = useAppSelector(state => state.context);
    const promotionsDataView = useAppSelector(state => getCurrentPromotionsDataView(state));
    const isClearingCart = useAppSelector(state => state.pages.cart.isClearingCart);
    const isUpdatingCartLine = useAppSelector(state => state.pages.cart.isUpdatingCartLine);
    const isRemovingCartLine = useAppSelector(
        state => Object.getOwnPropertyNames(state.pages.cart.isRemovingCartLine).length > 0,
    );

    const cartDataView = useCurrentCartState();
    const { isLoading, value: cart } = cartDataView;
    const cartLines = cart?.cartLines;
    const cartLinesRef = useRef(cartLines);
    if (cartLines) {
        cartLinesRef.current = cartLines;
    }
    const cartLinesToDisplay = cartLines ?? cartLinesRef.current;
    const itemsText = cartLinesToDisplay?.length === 1 ? translate("Item") : translate("Items");

    const isLoadingCart = Boolean(
        isLoading ||
            isUpdatingCart ||
            isUpdatingCartLine ||
            promotionsDataView.isLoading ||
            isRemovingCartLine ||
            isClearingCart,
    );
    const showCheckoutDisabledMessage =
        displayCheckoutButtons &&
        (isCartCheckoutDisabled(cart) || hasOnlyQuoteRequiredProducts(cart) || hasQuoteRequiredProducts(cart));
    const isMobile = useIsBreakpoint(mobileBreakpoint, "down");

    const scrollWrapperStyles = useMemo(() => {
        if (!maxVisibleItems) {
            return mergeProps<MiniCartStyles["scrollWrapper"]>(styles.scrollWrapper, {
                extendedStyles: {
                    wrapper: {
                        css: css`
                            max-height: initial;
                            overflow-y: initial;
                        `,
                    },
                },
            });
        }

        const containerMaxHeight = pxToRem(
            cartLineHeight * maxVisibleItems + (showCheckoutDisabledMessage ? infoBoxSpace : 0),
        );

        return mergeProps<MiniCartStyles["scrollWrapper"]>(styles.scrollWrapper, {
            alwaysShowDownGradient: Boolean(
                cartLinesToDisplay && cartLinesToDisplay.length > maxVisibleItems && !isMobile,
            ),
            extendedStyles: {
                wrapper: {
                    css: css`
                        ${breakpoint(mobileBreakpoint)} {
                            max-height: ${containerMaxHeight};
                        }
                    `,
                },
            },
        });
    }, [styles.scrollWrapper, maxVisibleItems, cartLinesToDisplay?.length, isMobile]);

    const cartLinesContainerStyles = useMemo(() => {
        if (!showCheckoutDisabledMessage) {
            return styles.cartLinesContainer;
        }

        return mergeProps<MiniCartStyles["cartLinesContainer"]>(styles.cartLinesContainer, {
            css: css`
                border-bottom: 1px solid ${getColor("common.border")};
            `,
        });
    }, [styles.cartLinesContainer, showCheckoutDisabledMessage]);

    const viewCartHandler = () => {
        onClose();
        cartLink && history.push(cartLink.url);
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.header}>
                <StyledWrapper {...styles.subtotalRow}>
                    <Typography {...styles.itemCount}>
                        {`${cart?.lineCount ?? 0} ${itemsText}`}
                        <VisuallyHidden>{translate("in your cart")}</VisuallyHidden>
                    </Typography>
                    <Typography {...styles.subtotal}>
                        {translate("Subtotal")}: {` ${cart?.orderSubTotalDisplay ?? "-"}`}
                    </Typography>
                </StyledWrapper>
                <Button {...styles.viewCartButton} disabled={isLoadingCart} onClick={viewCartHandler}>
                    {translate("View Cart")}
                </Button>
            </StyledWrapper>
            <DataViewLoadingOverlay {...styles.loadingOverlay} loading={isLoadingCart}>
                <ScrollWrapper {...scrollWrapperStyles}>
                    <StyledWrapper {...cartLinesContainerStyles}>
                        {cart?.lineCount === 0 ? (
                            <StyledWrapper {...styles.centeringWrapper}>
                                <Icon {...styles.noCartLinesIcon} />
                                <Typography {...styles.noCartLinesText}>{siteMessage("Cart_NoOrderLines")}</Typography>
                            </StyledWrapper>
                        ) : (
                            <>
                                {showCheckoutDisabledMessage && (
                                    <InfosBox {...styles.checkoutDisabledMessage}>
                                        {siteMessage("MinCart_CheckoutDisabled")}
                                    </InfosBox>
                                )}
                                <StyledWrapper
                                    tabIndex={-1}
                                    onClick={event => {
                                        const target = event.target as HTMLElement | null;
                                        if (target?.closest("a")) {
                                            onClose();
                                        }
                                    }}
                                >
                                    {cartLinesToDisplay?.map(cartLine => (
                                        <CartLineContext.Provider key={cartLine.id} value={cartLine}>
                                            <CartLineCardCondensed
                                                cart={cart!}
                                                editable={false}
                                                showInvalidPriceMessage={true}
                                                extendedStyles={styles.cartLineCardCondensed}
                                                showRemoveAction={!cartLine.isPromotionItem && cart?.canModifyOrder}
                                            />
                                        </CartLineContext.Provider>
                                    ))}
                                </StyledWrapper>
                            </>
                        )}
                    </StyledWrapper>
                </ScrollWrapper>
                {displayCheckoutButtons && !showCheckoutDisabledMessage ? (
                    <StyledWrapper
                        {...styles.footer}
                        onClick={event => {
                            const target = event.target as HTMLElement | null;
                            if (target?.closest("button")) {
                                onClose();
                            }
                        }}
                    >
                        <CartTotal
                            fields={{
                                showCartTotalDisplay: false,
                                showApprovalEditDraft: false,
                            }}
                            extendedStyles={styles.cartTotal}
                        />
                    </StyledWrapper>
                ) : null}
            </DataViewLoadingOverlay>
        </StyledWrapper>
    );
};

export default MiniCart;
